/**
 * StyleValidator.ts - Validate CSS variable values
 * 
 * CHANGE HISTORY:
 * - 251102_1845 danielbeer: Added advanced validation (display, position, flex, grid, ranges, units, browser compat)
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Validates CSS values for correctness with advanced property-specific rules
 */

import type { StyleVariables } from '../types';

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export interface ValidationError {
  variable: string;
  value: string | number;
  message: string;
  severity?: 'error' | 'warning';
  suggestion?: string;
}

export class StyleValidator {
  private strictMode: boolean;

  constructor(options: { strictMode?: boolean } = {}) {
    this.strictMode = options.strictMode ?? false;
  }

  /**
   * Validate all variables in a style
   */
  validate(variables: StyleVariables): ValidationResult {
    const errors: ValidationError[] = [];

    Object.entries(variables).forEach(([key, value]) => {
      const error = this.validateVariable(key, value);
      if (error) {
        errors.push(error);
      }
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate a single variable
   */
  validateVariable(key: string, value: string | number): ValidationError | null {
    const strValue = String(value);

    // Display property validation
    if (key.includes('display')) {
      return this.validateDisplay(key, strValue);
    }

    // Position property validation
    if (key.includes('position')) {
      return this.validatePosition(key, strValue);
    }

    // Flexbox property validation
    if (key.match(/flex-(direction|wrap|grow|shrink|basis|flow)/)) {
      return this.validateFlexProperty(key, strValue);
    }

    // Grid property validation
    if (key.match(/grid-(template|auto|gap|column|row)/)) {
      return this.validateGridProperty(key, strValue);
    }

    // Color validation
    if (key.includes('color') || key.includes('bg') || key.includes('text')) {
      if (!this.isValidColor(strValue)) {
        return {
          variable: key,
          value,
          message: `Invalid color value: ${strValue}`,
          severity: 'error',
          suggestion: 'Use hex (#fff), rgb(255,255,255), or named colors'
        };
      }
    }

    // Size validation with range checking
    if (key.includes('width') || key.includes('height') || key.includes('size')) {
      const sizeError = this.validateSizeWithRange(key, strValue);
      if (sizeError) return sizeError;
    }

    // Spacing validation
    if (key.includes('padding') || key.includes('margin') || key.includes('gap')) {
      if (!this.isValidSpacing(strValue)) {
        return {
          variable: key,
          value,
          message: `Invalid spacing value: ${strValue}`,
          severity: 'error',
          suggestion: 'Use valid units: px, em, rem, %, vh, vw'
        };
      }
    }

    // Opacity validation
    if (key.includes('opacity')) {
      return this.validateOpacity(key, strValue);
    }

    // Z-index validation
    if (key.includes('z-index')) {
      return this.validateZIndex(key, strValue);
    }

    // Font weight validation
    if (key.includes('font-weight')) {
      return this.validateFontWeight(key, strValue);
    }

    // Line height validation
    if (key.includes('line-height')) {
      return this.validateLineHeight(key, strValue);
    }

    return null;
  }

  /**
   * Check if value is a valid color
   */
  private isValidColor(value: string): boolean {
    // Allow CSS variables
    if (value.startsWith('var(')) return true;

    // Hex colors
    if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) return true;

    // RGB/RGBA
    if (/^rgba?\(/.test(value)) return true;

    // HSL/HSLA
    if (/^hsla?\(/.test(value)) return true;

    // Named colors (basic check)
    const namedColors = ['transparent', 'currentColor', 'inherit', 'initial', 'unset'];
    if (namedColors.includes(value.toLowerCase())) return true;

    return false;
  }

  /**
   * Check if value is a valid size
   */
  private isValidSize(value: string): boolean {
    // Allow CSS variables
    if (value.startsWith('var(')) return true;

    // Allow keywords
    const keywords = ['auto', 'inherit', 'initial', 'unset', 'fit-content', 'max-content', 'min-content'];
    if (keywords.includes(value.toLowerCase())) return true;

    // Check for valid units
    if (/^\d+(\.\d+)?(px|em|rem|%|vh|vw|vmin|vmax|ch|ex)$/.test(value)) return true;

    return false;
  }

  /**
   * Check if value is valid spacing
   */
  private isValidSpacing(value: string): boolean {
    // Allow CSS variables
    if (value.startsWith('var(')) return true;

    // Allow keywords
    if (['auto', 'inherit', 'initial', 'unset'].includes(value.toLowerCase())) return true;

    // Single value
    if (/^\d+(\.\d+)?(px|em|rem|%|vh|vw)$/.test(value)) return true;

    // Multiple values (e.g., "10px 20px")
    const parts = value.split(/\s+/);
    if (parts.every(p => /^\d+(\.\d+)?(px|em|rem|%|vh|vw)$/.test(p))) return true;

    return false;
  }

  /**
   * Validate display property
   */
  private validateDisplay(key: string, value: string): ValidationError | null {
    const validValues = [
      'block', 'inline', 'inline-block', 'flex', 'inline-flex',
      'grid', 'inline-grid', 'table', 'table-row', 'table-cell',
      'none', 'contents', 'flow-root', 'inherit', 'initial', 'unset'
    ];

    if (value.startsWith('var(')) return null;

    if (!validValues.includes(value.toLowerCase())) {
      return {
        variable: key,
        value,
        message: `Invalid display value: ${value}`,
        severity: 'error',
        suggestion: `Valid values: ${validValues.slice(0, 5).join(', ')}, ...`
      };
    }

    return null;
  }

  /**
   * Validate position property
   */
  private validatePosition(key: string, value: string): ValidationError | null {
    const validValues = ['static', 'relative', 'absolute', 'fixed', 'sticky', 'inherit', 'initial', 'unset'];

    if (value.startsWith('var(')) return null;

    if (!validValues.includes(value.toLowerCase())) {
      return {
        variable: key,
        value,
        message: `Invalid position value: ${value}`,
        severity: 'error',
        suggestion: `Valid values: ${validValues.join(', ')}`
      };
    }

    // Browser compatibility warning for sticky
    if (value.toLowerCase() === 'sticky' && !this.strictMode) {
      return {
        variable: key,
        value,
        message: 'position: sticky may not work in older browsers',
        severity: 'warning',
        suggestion: 'Consider fallback for IE11 and older'
      };
    }

    return null;
  }

  /**
   * Validate flexbox properties
   */
  private validateFlexProperty(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;

    if (key.includes('flex-direction')) {
      const validValues = ['row', 'row-reverse', 'column', 'column-reverse', 'inherit', 'initial', 'unset'];
      if (!validValues.includes(value.toLowerCase())) {
        return {
          variable: key,
          value,
          message: `Invalid flex-direction: ${value}`,
          severity: 'error',
          suggestion: `Valid values: ${validValues.join(', ')}`
        };
      }
    }

    if (key.includes('flex-wrap')) {
      const validValues = ['nowrap', 'wrap', 'wrap-reverse', 'inherit', 'initial', 'unset'];
      if (!validValues.includes(value.toLowerCase())) {
        return {
          variable: key,
          value,
          message: `Invalid flex-wrap: ${value}`,
          severity: 'error',
          suggestion: `Valid values: ${validValues.join(', ')}`
        };
      }
    }

    if (key.includes('flex-grow') || key.includes('flex-shrink')) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        return {
          variable: key,
          value,
          message: `${key} must be a non-negative number`,
          severity: 'error',
          suggestion: 'Use 0, 1, 2, etc.'
        };
      }
    }

    return null;
  }

  /**
   * Validate grid properties
   */
  private validateGridProperty(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;

    // Grid template validation (basic)
    if (key.includes('grid-template')) {
      // Allow complex grid template syntax
      if (value.includes('repeat(') || value.includes('minmax(') || value.includes('fr')) {
        return null; // Complex syntax, assume valid
      }
    }

    // Grid auto-flow validation
    if (key.includes('grid-auto-flow')) {
      const validValues = ['row', 'column', 'row dense', 'column dense', 'dense', 'inherit', 'initial', 'unset'];
      if (!validValues.includes(value.toLowerCase())) {
        return {
          variable: key,
          value,
          message: `Invalid grid-auto-flow: ${value}`,
          severity: 'error',
          suggestion: `Valid values: ${validValues.join(', ')}`
        };
      }
    }

    return null;
  }

  /**
   * Validate size with range checking
   */
  private validateSizeWithRange(key: string, value: string): ValidationError | null {
    if (!this.isValidSize(value)) {
      return {
        variable: key,
        value,
        message: `Invalid size value: ${value}`,
        severity: 'error',
        suggestion: 'Use valid units: px, em, rem, %, vh, vw'
      };
    }

    // Extract numeric value and unit
    const match = value.match(/^(-?\d+(\.\d+)?)(px|em|rem|%|vh|vw)?$/);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[3];

      // Warn about negative sizes (except for certain properties)
      if (num < 0 && !key.includes('margin') && !key.includes('top') && !key.includes('left') && !key.includes('right') && !key.includes('bottom')) {
        return {
          variable: key,
          value,
          message: `Negative size may cause layout issues: ${value}`,
          severity: 'warning',
          suggestion: 'Consider using 0 or positive values'
        };
      }

      // Warn about very large percentage values
      if (unit === '%' && num > 100) {
        return {
          variable: key,
          value,
          message: `Percentage over 100% may cause overflow: ${value}`,
          severity: 'warning',
          suggestion: 'Consider using max-width/max-height or smaller percentage'
        };
      }

      // Warn about very large pixel values
      if (unit === 'px' && num > 10000) {
        return {
          variable: key,
          value,
          message: `Very large pixel value: ${value}`,
          severity: 'warning',
          suggestion: 'Consider using viewport units (vh/vw) for large sizes'
        };
      }
    }

    return null;
  }

  /**
   * Validate opacity (0-1 range)
   */
  private validateOpacity(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;

    const num = parseFloat(value);
    if (isNaN(num)) {
      return {
        variable: key,
        value,
        message: `Opacity must be a number: ${value}`,
        severity: 'error',
        suggestion: 'Use values between 0 and 1 (e.g., 0.5)'
      };
    }

    if (num < 0 || num > 1) {
      return {
        variable: key,
        value,
        message: `Opacity must be between 0 and 1: ${value}`,
        severity: 'error',
        suggestion: 'Use 0 (transparent) to 1 (opaque)'
      };
    }

    return null;
  }

  /**
   * Validate z-index
   */
  private validateZIndex(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;
    if (['auto', 'inherit', 'initial', 'unset'].includes(value.toLowerCase())) return null;

    const num = parseInt(value, 10);
    if (isNaN(num)) {
      return {
        variable: key,
        value,
        message: `z-index must be an integer: ${value}`,
        severity: 'error',
        suggestion: 'Use whole numbers (e.g., 1, 10, 100)'
      };
    }

    if (num > 9999) {
      return {
        variable: key,
        value,
        message: `Very high z-index value: ${value}`,
        severity: 'warning',
        suggestion: 'Consider using lower values to avoid stacking context issues'
      };
    }

    return null;
  }

  /**
   * Validate font-weight
   */
  private validateFontWeight(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;

    const validKeywords = ['normal', 'bold', 'bolder', 'lighter', 'inherit', 'initial', 'unset'];
    if (validKeywords.includes(value.toLowerCase())) return null;

    const num = parseInt(value, 10);
    if (isNaN(num)) {
      return {
        variable: key,
        value,
        message: `Invalid font-weight: ${value}`,
        severity: 'error',
        suggestion: 'Use 100-900 (multiples of 100) or keywords (normal, bold)'
      };
    }

    if (num < 100 || num > 900 || num % 100 !== 0) {
      return {
        variable: key,
        value,
        message: `font-weight should be 100-900 in multiples of 100: ${value}`,
        severity: 'warning',
        suggestion: 'Use 100, 200, 300, 400 (normal), 500, 600, 700 (bold), 800, 900'
      };
    }

    return null;
  }

  /**
   * Validate line-height
   */
  private validateLineHeight(key: string, value: string): ValidationError | null {
    if (value.startsWith('var(')) return null;
    if (['normal', 'inherit', 'initial', 'unset'].includes(value.toLowerCase())) return null;

    // Unitless number (recommended)
    const unitless = parseFloat(value);
    if (!isNaN(unitless) && value === String(unitless)) {
      if (unitless < 0.5 || unitless > 3) {
        return {
          variable: key,
          value,
          message: `Unusual line-height value: ${value}`,
          severity: 'warning',
          suggestion: 'Typical range is 1.2 to 1.8 for body text'
        };
      }
      return null;
    }

    // With units
    if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(value)) {
      return null;
    }

    return {
      variable: key,
      value,
      message: `Invalid line-height: ${value}`,
      severity: 'error',
      suggestion: 'Use unitless number (1.5) or with units (24px, 1.5em)'
    };
  }
}

let validatorInstance: StyleValidator | null = null;

export function getStyleValidator(): StyleValidator {
  if (!validatorInstance) {
    validatorInstance = new StyleValidator();
  }
  return validatorInstance;
}
